import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { ApiService } from '../../../services/api.service';
import { getEncoding } from 'js-tiktoken';
import { AI_SYSTEM_MESSAGES } from '../ai-chat.constant';
import { ProfileService } from '../../../services/profile.service';
import { ChatSessionService } from './chat-session.service';
import { ChatMessageService } from './chat-message.service';
import { ChatDocumentService } from './chat-document.service';
import { ChatRole, RoutePath } from '../../../enums/api.enum';
import { Router } from '@angular/router';
import { IChatSession, IMessage } from '../../../interfaces/i-chat-session';
import { ISensitivityLabel } from '../../../interfaces/i-sensitivity-label';
import { AutomationStateService } from './automation-state.service';
import { PromptLibraryService } from './prompt-library.service';
import { GENERIC_ERROR_MESSAGE } from "../../../constants/api.constant";
import { AgentService } from '../../agent/services/agent.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { AlertEnum } from '../../../shared/alert/alert.enum';
import { ChatUtilitiesService } from "./chat-utilities.service";
import { AutomationExecutionService } from "./automation-execution.service";
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DocumentManagerComponent } from '../document-manager/document-manager.component';
import { ExportChatSessionService } from '../../../services/export-chat-session.service';
import { ConversationStarterService } from './conversation-starter.service';
import { MultiSessionService } from '../../../services/multi-session.service';

@Injectable({
  providedIn: 'root',
})
export class AiChatService {
  pollEntriesInterval: any = null;
  // Map in-flight assistant messageId -> owning sessionId to avoid reliance on selected session
  #inflightByMessageId = new Map<string, { sessionId: string | number, expiresAt: number }>();

  #authService = inject(AuthService);
  #multiSessionService = inject(MultiSessionService);
  #apiService = inject(ApiService);
  #router = inject(Router);
  #tiktokenEncoder = getEncoding("cl100k_base");
  #messageId = '';
  #meta: any = {};
  #selectedSessionId: string | number = '';

  readonly dialog = inject(MatDialog);

  #profileService = inject(ProfileService);
  #chatSessionService = inject(ChatSessionService);
  #chatMessageService = inject(ChatMessageService);
  #chatDocumentService = inject(ChatDocumentService);
  #automationStateService = inject(AutomationStateService);
  #automationExecutionService = inject(AutomationExecutionService);
  #agentService = inject(AgentService);
  #alertService = inject(AlertService);
  #promptLibraryService = inject(PromptLibraryService);
  #chatUtilitiesService = inject(ChatUtilitiesService);
  #exportChatSessionService = inject(ExportChatSessionService);
  #conversationStarterService = inject(ConversationStarterService);

  constructor() { }

  /**
   * Sends a message to the chat API and handles the response.
   * @param {string} content - The message content to send
   * @param {ChatRole} chatRole - The role of the sender (optional)
   * @returns {Promise<boolean | undefined>}
   */
  async sendMessage(content: string, chatRole?: ChatRole, sessionId?: string | number): Promise<boolean | undefined> {
    // Use provided sessionId or fall back to selected session id
    const currentSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();

    // Mark this session as active submitting
    if (currentSessionId) {
      this.#multiSessionService.startSession(currentSessionId);
    }

    try {
      const accessToken = this.#authService.accessToken();
      if (!accessToken) {
        this.#authService.signIn();
        return;
      }

      const currentUserId = this.#profileService.userProfile()?.profile.name!;
      this.#selectedSessionId = currentSessionId;
      const maximumMessagesToUse = this.#profileService.userProfile()?.config?.maximumMessagesToUse!;

      this.#meta = this.#chatDocumentService.selectedDocuments().length > 0 ? { documents: this.#chatDocumentService.selectedDocuments() } : null;
      // this.#chatDocumentService.selectedDocuments.set([]);  // Leaving this here for now in case there are some unforeseen side effects..  It is now in #pollEntries().
      this.#chatDocumentService.storeSelectedDocuments(String(this.#selectedSessionId), this.#chatDocumentService.selectedDocuments());

      // Add user message - handle both selected and non-selected sessions
      const userMsgParams = {
        senderId: currentUserId,
        role: chatRole ?? ChatRole.User,
        content,
        canSubmit: true,
        canCopy: content !== 'Please continue',
        meta: this.#meta,
        currentUserId
      };

      // If we are in the active session add the message to the session and UI,
      // else, just add it to that particular session's messages array.
      if (this.#chatSessionService.selectedSessionId() === currentSessionId) {
        this.#chatMessageService.addMessage(userMsgParams);
        // Sync to session object immediately
        const owningSession = this.#chatSessionService.selectedSession();
        if (owningSession?.id === currentSessionId) {
          owningSession.messages = this.#chatMessageService.messages() || [];
        }
      } else {
        const newMsg = this.#chatMessageService.createMessage(userMsgParams);

        // Update the chatSessions signal immutably instead of mutating the session object
        this.#chatSessionService.chatSessions.update(sessions => {
          if (!sessions) return sessions;
          return sessions.map(s => {
            if (s.id === currentSessionId) {
              return {
                ...s,
                messages: [...(s.messages || []), newMsg]
              };
            }
            return s;
          });
        });
      }

      // Check if we are in the middle of a file upload workflow instruction.
      // We are not waiting for user input anymore, so update the chat session configuration.
      if (this.#automationStateService.isFileUploadInProgress()) {
        const instruction = this.#automationStateService.instructionInProgress();
        await this.#automationExecutionService.updateChatSessionConfiguration(instruction!);

      }

      // Add system message with configuration updates if provided
      // First check for instruction in the current session's input data
      const currentInputData = currentSessionId
        ? this.#chatSessionService.getSessionInputData(currentSessionId)
        : null;

      // Use session-specific instruction if available, otherwise fall back to global state
      const instructions = currentInputData?.instruction;

      if (instructions) {
        // If using session input data instruction, set it as the current instruction for consistency
        if (currentInputData?.instruction) {
          this.#automationStateService.instructionInProgress.set(currentInputData.instruction);
        }

        // Update chat session with the instruction configuration - await this call to ensure
        // config update message appears before assistant message
        await this.#promptLibraryService.updateChatSessionWithSelectedPrompt(instructions);
      }

      // Get messages from the target session (could be selected or non-selected)
      const sessionForPrompt = this.#chatSessionService.chatSessions()?.find(s => s.id === currentSessionId);
      const sessionMessages = sessionForPrompt?.messages || [];

      const promptMessages = [
        { role: ChatRole.System, content: AI_SYSTEM_MESSAGES.currentSystemMessage },
        ...sessionMessages
          .filter((msg) => msg.senderId === currentUserId || msg.canSubmit)
          .slice(-maximumMessagesToUse)
          .map((msg) => ({ role: msg.role, content: msg.clipboardVersion, meta: msg.meta })),
      ];

      // Add the assistant message after configuration updates have been applied
      const assistantMsgParams = {
        senderId: ChatRole.Assistant,
        role: ChatRole.Assistant,
        content: '',
        useLoadingMessage: true,
        currentUserId
      };

      let messageId = '';

      if (this.#chatSessionService.selectedSessionId() === currentSessionId) {
        const assistantMsg = this.#chatMessageService.addMessage(assistantMsgParams);
        messageId = assistantMsg.id!;
        // Sync to session object
        const owningSession = this.#chatSessionService.selectedSession();
        if (owningSession?.id === currentSessionId) {
          owningSession.messages = this.#chatMessageService.messages() || [];
        }
      } else {
        const assistantMsg = this.#chatMessageService.createMessage(assistantMsgParams);
        messageId = assistantMsg.id!;

        // Update the chatSessions signal immutably instead of mutating the session object
        this.#chatSessionService.chatSessions.update(sessions => {
          if (!sessions) return sessions;
          return sessions.map(s => {
            if (s.id === currentSessionId) {
              return {
                ...s,
                messages: [...(s.messages || []), assistantMsg]
              };
            }
            return s;
          });
        });
      }

      this.#messageId = messageId;
      // Record which session owns the in progress AI assisntant message
      if (this.#selectedSessionId) {
        // Set a 2-minute expiry for safety; server should complete sooner
        const expiresAt = Date.now() + 2 * 60 * 1000;
        this.#inflightByMessageId.set(this.#messageId, { sessionId: this.#selectedSessionId, expiresAt });
      }

      // First save call - persist the session that actually has the new messages
      const sessionToPersist = this.#chatSessionService.chatSessions()?.find(s => s.id === currentSessionId);
      if (sessionToPersist) {
        await firstValueFrom(this.#apiService.persistSession(sessionToPersist));
      }

      // Add message to the queue with expiry time
      this.#chatMessageService.addMessageToQueue(this.#messageId);
      const response = await firstValueFrom(this.#apiService.chat(this.#messageId, promptMessages, String(this.#selectedSessionId)));
      this.#handleResponse(response, this.#messageId, content, this.#meta, String(this.#selectedSessionId));

      // Start polling for stream responses
      let pollingPromise: Promise<boolean> = Promise.resolve(false);
      if (this.pollEntriesInterval == null) {
        pollingPromise = new Promise<boolean>((resolve) => {
          this.pollEntriesInterval = setInterval(async () => {
            await this.#pollEntriesMulti();
            if (!this.pollEntriesInterval) {
              resolve(this.#multiSessionService.isSessionStreaming(currentSessionId));
            }
          }, 1000);
        });
      }

      return pollingPromise;
    } catch (error) {
      this.#handleError(this.#messageId, content, this.#meta, String(this.#selectedSessionId));
      this.#handleErrorMessaging(error, content, this.#meta, String(this.#selectedSessionId));
      return false;
    } finally {
      // Do not clear active state here.
      // only clear when the message actually completes or errors out.
    }
  }

  /**
   * Cancels retries for a given message.
   * Removes the message from the messages signal and the message queue.
   * Updates selected documents and stores them, then persists the session.
   */
  cancelRetries(message: IMessage | undefined, removeSelectedDocs = true): void {
    // Set the Session status of "is Submitting" to false BEFORE restoring prompt so the ai-chat-prompt component effect doesn't clear it
    this.#multiSessionService.cancelSession(this.#chatSessionService.selectedSessionId());

    // If there's no message to cancel, clear any pending queue entry and exit gracefully
    if (!message) {
      this.#chatMessageService.messageQueue.set(undefined);
      return;
    }

    // Remove the message from the messages signal
    this.#chatMessageService.messages.update(messages => {
      if (messages) {
        return messages.filter(o => o.id !== message.id);
      } else {
        return [];
      }
    });

    // Remove the message from the messageQueue signal
    this.#chatMessageService.messageQueue.set(undefined);

    // Find the last user message
    const lastMessage = this.#chatMessageService.messages()!.slice().reverse().find(o => o.role === 'user');
    if (lastMessage) {
      // Restore the user message to the input area
      const sessionId = this.#chatSessionService.selectedSession()?.id!;
      const currentInputData = this.#chatSessionService.getSessionInputData(sessionId);
      const documentsToRestore = lastMessage.meta?.documents ?? [];

      this.#chatSessionService.setSessionInputData(sessionId, {
        text: lastMessage.clipboardVersion ?? lastMessage.content ?? '',
        documents: documentsToRestore,
        instruction: currentInputData.instruction
      });

      // Update selected documents for the UI to show the attachments
      this.#chatDocumentService.selectedDocuments.set(documentsToRestore);
      this.#chatDocumentService.storeSelectedDocuments(sessionId, documentsToRestore);

      if (removeSelectedDocs) {
        // Note: removeSelectedDocs is used for other cleanup, but we still want to restore
        // the documents to the input area so the user can retry
      }
      // Remove the last user message from the messages signal
      this.#chatMessageService.messages.update(messages => {
        if (messages) {
          return messages.filter(o => o.id !== lastMessage.id);
        } else {
          return [];
        }
      });
    }

    // TODO update this to restore session config if we used an instruction or workflow
    // If the prompt was from the Automation Library and we have a saved configuration,
      // we need to restore it to prevent unwanted changes from being persisted after errors
      if (this.#automationStateService.instructionInProgress() &&
          this.#chatSessionService.selectedSession()?.snapshotConfiguration) {
            this.#chatUtilitiesService.restoreSessionConfig();
      }

    // Persist the session
    this.#apiService.persistSession(this.#chatSessionService.selectedSession()!);
  }

  /**
   * Handles the response from the chat API.
   */
  #handleResponse(response: any, messageId: string, content: string, meta: any, selectedSessionId: string): void {
    if (response.error) {
      // Set the Session status of "is Submitting" to false BEFORE restoring prompt so the ai-chat-prompt component effect doesn't clear it
      this.#multiSessionService.errorSession(selectedSessionId);

      // Update messages to remove the message with the given messageId
      this.#chatMessageService.messages.update(messages => {
        if (messages) {
          return messages.filter(o => o.id !== messageId);
        } else {
          return [];
        }
      });

      // Add a new system message with the error content
      this.#chatMessageService.addMessage({
        senderId: ChatRole.System,
        role: ChatRole.System,
        content: GENERIC_ERROR_MESSAGE,
        system: true,
        error: true,
        currentUserId: this.#profileService.userProfile()?.profile.name!,
        showExpansionPanel: true,
        expansionPanelTitle: 'Error Processing Prompt',
      });

      // Restore the user message to the input area
      if (content !== 'Please continue') {
        const currentInputData = this.#chatSessionService.getSessionInputData(selectedSessionId);
        this.#chatSessionService.setSessionInputData(selectedSessionId, {
          text: content,
          documents: meta?.documents ?? [],
          instruction: currentInputData.instruction
        });

        // Update selected documents for the UI
        this.#chatDocumentService.selectedDocuments.set(meta?.documents ?? []);
        this.#chatDocumentService.storeSelectedDocuments(selectedSessionId, this.#chatDocumentService.selectedDocuments());
      }
    }
  }

  /**
 * Handles errors that occur during the sendMessage process.
 * Updates the messages to remove the message with the given messageId.
 * Prompt restoration is handled by #handleErrorMessaging.
 */
  #handleError(messageId: string, content: string, meta: any, selectedSessionId: string): void {
    // Update messages to remove the message with the given messageId
    this.#chatMessageService.messages.update(messages => {
      if (messages) {
        return messages.filter(o => o.id !== messageId);
      } else {
        return [];
      }
    });
  }

  /**
 * Handles error messaging based on the error response.
 * Adds appropriate system messages, handles redirection, and restores user prompt if needed.
 */
  #handleErrorMessaging(err: any, content?: string, meta?: any, selectedSessionId?: string): void {
    if (selectedSessionId) {
      // Set the Session status of "is Submitting" to false BEFORE restoring prompt so the ai-chat-prompt component effect doesn't clear it
      this.#multiSessionService.errorSession(selectedSessionId);
    }

    const isAuthError = err?.response?.status === 401 || err?.response?.status === 403;

    if (isAuthError) {
      this.#chatMessageService.addMessage({
        senderId: ChatRole.System,
        role: ChatRole.System,
        content: GENERIC_ERROR_MESSAGE,
        system: true,
        error: true,
        currentUserId: this.#profileService.userProfile()?.profile.name!,
        showExpansionPanel: true,
        expansionPanelTitle: 'Error Processing Prompt',
      });
      this.#router.navigate([RoutePath.SignIn]);
      // Skip prompt restoration since user is being redirected to sign-in
      return;
    } else if (err?.response?.status === 422) {
      this.#chatMessageService.addMessage({
        senderId: ChatRole.System,
        role: ChatRole.System,
        content: GENERIC_ERROR_MESSAGE,
        system: true,
        error: true,
        currentUserId: this.#profileService.userProfile()?.profile.name!,
        showExpansionPanel: true,
        expansionPanelTitle: 'Error Processing Prompt',
      });

      this.#chatUtilitiesService.restoreSessionConfig(true); // Restore session config on error
    } else {
      this.#chatMessageService.addMessage({
        senderId: ChatRole.System,
        role: ChatRole.System,
        content: GENERIC_ERROR_MESSAGE,
        system: true,
        error: true,
        currentUserId: this.#profileService.userProfile()?.profile.name!,
        showExpansionPanel: true,
        expansionPanelTitle: 'Error Processing Prompt',
      });

      this.#chatUtilitiesService.restoreSessionConfig(true); // Restore session config on error
    }

    // Restore the user message to the input area if available
    if (content && content !== 'Please continue' && selectedSessionId) {
      const currentInputData = this.#chatSessionService.getSessionInputData(selectedSessionId);
      this.#chatSessionService.setSessionInputData(selectedSessionId, {
        text: content,
        documents: meta?.documents ?? [],
        instruction: currentInputData.instruction
      });

      // Update selected documents for the UI
      this.#chatDocumentService.selectedDocuments.set(meta?.documents ?? []);
      this.#chatDocumentService.storeSelectedDocuments(selectedSessionId, this.#chatDocumentService.selectedDocuments());
    }
  }

  /**
 * Polls for stream responses from the chat API.
 * Handles the progress and completion of responses, updating messages accordingly.
 * Stops polling if no message queue exists or if the message queue has expired.
 * TODO commented out since it is no longer needed.
 * Need to determine if it is safe to delete.
 */
  // async #pollEntries(): Promise<void> {
  //   const now = new Date();
  //   if (!this.#chatMessageService.messageQueue()) {
  //     this.#stopPollingEntries();
  //     return;
  //   }

  //   if (new Date(this.#chatMessageService.messageQueue()!.expiry) < now) {
  //     const msg = this.#chatMessageService.messages()!.find(p => p.id === this.#chatMessageService.messageQueue()!.messageId);
  //     if (msg) {
  //       msg.formatted = 'No response';
  //       msg.useLoadingMessage = false;
  //     }
  //     this.#chatMessageService.messageQueue.set(undefined);
  //   }

  //   if (!this.#chatMessageService.messageQueue()) {
  //     this.#stopPollingEntries();
  //     return;
  //   }

  //   try {
  //     const messageId = this.#chatMessageService.messageQueue()!.messageId;
  //     const responses = await firstValueFrom(this.#apiService.stream([messageId]));

  //     if (responses && responses.length > 0) {
  //       for (const response of responses) {
  //         if (response.progress) {
  //           const progress = response.progress;
  //           // Resolve target session for this response using the in-flight map.
  //           // fallback will be to the currently selected session
  //           const mapped = this.#inflightByMessageId.get(response.messageId);
  //           const targetSessionId = mapped?.sessionId ?? this.#chatSessionService.selectedSessionId();
  //           const allSessions = this.#chatSessionService.chatSessions();
  //           const targetSession = allSessions?.find(s => s.id === targetSessionId);
  //           const locatedMessage = targetSession?.messages?.find(p => p.id === response.messageId)
  //             ?? this.#chatMessageService.messages()?.find(p => p.id === response.messageId);
  //           if (locatedMessage) {
  //             // If progress.completed === true, then progress has the answer
  //             if (progress.completed === true) {
  //               locatedMessage.progressDetail = null;
  //               let isError = false;

  //               if (progress.messages && progress.messages.length > 0) {
  //                 const truncatedResult = progress.truncated === true;
  //                 let answer = progress.messages.filter(o => o.role === "assistant")?.[0]?.content;
  //                 let parsedContent = this.#parseChoiceMessage({
  //                   answer,
  //                   citations: progress.messages.filter(o => o.role === "tool").map(o => JSON.parse(o.content))?.[0]?.citations,
  //                 });
  //                 let markdownText = parsedContent.markdownFormatText;
  //                 // TODO not in use yet. apply this to the ngx-markdown/Prismjs library when we use citations.
  //                 // if (parsedContent.citations.length > 0) {
  //                 //   markdownText += '  \n\n';

  //                 //   parsedContent.citations.forEach((o, idx) => {
  //                 //     markdownText += `${idx + 1}: [${o.title ?? o.filepath}](${o.url})  \n\n`;
  //                 //   });
  //                 // }
  //                 locatedMessage.formatted = markdownText;
  //                 locatedMessage.clipboardVersion = answer;
  //                 locatedMessage.canSubmit = true;
  //                 locatedMessage.canCopy = true;
  //                 locatedMessage.isTruncated = truncatedResult;
  //                 locatedMessage.requestDetail = progress.details;
  //                 locatedMessage.useLoadingMessage = false;
  //                 if (progress.attachments && progress.attachments.length > 0) {
  //                   locatedMessage.meta = { documents: progress.attachments };
  //                 }

  //               } else if (progress.error === true) {
  //                 isError = true;
  //                 if (progress.highLoad === true) {
  //                   const loadingMessage = this.#chatMessageService.messages()!.find(o => o.useLoadingMessage === true);
  //                   if (loadingMessage) {
  //                     loadingMessage.formatted = progress.answer;
  //                   }
  //                 } else {
  //                   this.cancelRetries(locatedMessage);
  //                   this.#chatMessageService.addMessage({
  //                     senderId: ChatRole.System,
  //                     role: ChatRole.System,
  //                     content: GENERIC_ERROR_MESSAGE,
  //                     system: true,
  //                     error: true,
  //                     showExpansionPanel: true,
  //                     expansionPanelTitle: 'Error Processing Prompt',
  //                   });
  //                 }
  //               } else {
  //                 const updatedMessages = this.#chatMessageService.messages()!.filter(o => o.useLoadingMessage !== true);
  //                 this.#chatMessageService.messages.set(updatedMessages);
  //               }

  //               // Ensure the updated message is persisted to the owning session
  //               if (targetSession) {
  //                 // If the currently selected session is the target, ensure session array includes latest ChatMessageService messages
  //                 const currentSelectedId = this.#chatSessionService.selectedSessionId();
  //                 if (currentSelectedId === targetSessionId) {
  //                   const currentMessages = this.#chatMessageService.messages();
  //                   if (currentMessages) {
  //                     // Merge new messages that aren't already in the target session
  //                     if (!targetSession.messages) {
  //                       targetSession.messages = [];
  //                     }
  //                     const newMessages = currentMessages.filter(msg => !targetSession.messages.some(existingMsg => existingMsg.id === msg.id));
  //                     if (newMessages.length > 0) {
  //                       targetSession.messages.push(...newMessages);
  //                     }
  //                   }
  //                 }

  //                 await firstValueFrom(this.#apiService.persistSession(targetSession));
  //               }

  //               // Only clear input data and selected documents on successful completion, not on errors
  //               if (!isError) {
  //                 // Only clear UI-selected documents if the owning session is currently selected
  //                 if (this.#chatSessionService.selectedSessionId() === targetSessionId) {
  //                   this.#chatDocumentService.selectedDocuments.set([]);
  //                 }
  //                 // Clear the Session Input Data for the owning session
  //                 if (targetSessionId) {
  //                   this.#chatSessionService.setSessionInputData(targetSessionId, {
  //                     text: '',
  //                     documents: [],
  //                     instruction: undefined
  //                   });
  //                 }
  //               }

  //               // Remove from in-flight map now that it's finished
  //               this.#inflightByMessageId.delete(response.messageId);
  //               // Stop polling after receiving the completed response
  //               this.#stopPollingEntries(isError);
  //             } else {
  //               locatedMessage.formatted = progress.answer ?? 'Processing...';
  //               locatedMessage.progressDetail = progress;
  //             }
  //           }
  //         } else {
  //           const msg = this.#chatMessageService.messages()!.find(p => p.id === response.messageId);
  //           if (msg) {
  //             msg.formatted = 'No response';
  //             msg.useLoadingMessage = false;
  //           }
  //           this.#chatMessageService.messageQueue.set(undefined);
  //           this.#stopPollingEntries();
  //         }
  //       }
  //     } else {
  //       const msg = this.#chatMessageService.messages()!.find(p => p.id === this.#chatMessageService.messageQueue()!.messageId);
  //       if (msg) {
  //         msg.formatted = 'No response';
  //         msg.useLoadingMessage = false;
  //       }
  //       this.#chatMessageService.messageQueue.set(undefined);
  //       this.#stopPollingEntries();
  //     }
  //   } catch (err) {
  //     console.error('Error in polling:', err);
  //     const msg = this.#chatMessageService.messages()!.find(p => p.id === this.#chatMessageService.messageQueue()!.messageId);
  //     if (msg) {
  //       msg.formatted = 'No response';
  //       msg.useLoadingMessage = false;
  //     }
  //     this.#chatMessageService.messageQueue.set(undefined);
  //     this.#stopPollingEntries(true);
  //     // For polling errors, don't restore prompt as the user message is already in the chat history
  //     this.#handleErrorMessaging(err);
  //   }
  // }

  /**
   * Polls all in-flight messageIds in a single batched request and updates their owning sessions.
   */
  async #pollEntriesMulti(): Promise<void> {
    // If nothing in-flight, stop polling
    if (this.#inflightByMessageId.size === 0) {
      this.#stopPollingEntries();
      return;
    }

    // Drop any expired entries
    const nowTs = Date.now();
    for (const [mid, info] of Array.from(this.#inflightByMessageId.entries())) {
      if (info.expiresAt <= nowTs) {
        const owningSession = this.#chatSessionService.chatSessions()?.find(s => s.id === info.sessionId);
        if (owningSession) {
          const msg = owningSession.messages?.find(m => m.id === mid);
          if (msg) {
            msg.formatted = 'No response';
            msg.useLoadingMessage = false;
          }
        }
        this.#inflightByMessageId.delete(mid);
      }
    }

    if (this.#inflightByMessageId.size === 0) {
      this.#stopPollingEntries();
      return;
    }

    const messageIds = Array.from(this.#inflightByMessageId.keys());
    try {
      const responses = await firstValueFrom(this.#apiService.stream(messageIds));

      if (!responses || responses.length === 0) {
        // no updates this tick; do nothing
        return;
      }

      for (const response of responses) {
        const inflight = this.#inflightByMessageId.get(response.messageId);
        if (!inflight) {
          continue;
        }
        const targetSession = this.#chatSessionService.chatSessions()?.find(s => s.id === inflight.sessionId);

        if (response.progress) {
          const progress = response.progress;
          const locatedMessage = targetSession?.messages?.find(p => p.id === response.messageId);
          if (!locatedMessage) {
            continue;
          }

          if (progress.completed === true) {
            locatedMessage.progressDetail = null;
            let isError = false;

            if (progress.messages && progress.messages.length > 0) {
              const truncatedResult = progress.truncated === true;
              const answer = progress.messages.filter((o: any) => o.role === 'assistant')?.[0]?.content;
              const parsedContent = this.#parseChoiceMessage({
                answer,
                citations: progress.messages.filter((o: any) => o.role === 'tool').map((o: any) => JSON.parse(o.content))?.[0]?.citations,
              });
              const markdownText = parsedContent.markdownFormatText;
              locatedMessage.formatted = markdownText;
              locatedMessage.clipboardVersion = answer;
              locatedMessage.canSubmit = true;
              locatedMessage.canCopy = true;
              locatedMessage.isTruncated = truncatedResult;
              locatedMessage.requestDetail = progress.details;
              locatedMessage.useLoadingMessage = false;
              if (progress.attachments && progress.attachments.length > 0) {
                locatedMessage.meta = { documents: progress.attachments };
              }
            } else if (progress.error === true) {
              isError = true;
              if (progress.highLoad === true) {
                locatedMessage.formatted = progress.answer;
              } else {
                // Remove the in-progress assistant message from the owning session
                if (targetSession?.messages) {
                  targetSession.messages = targetSession.messages.filter(m => m.id !== locatedMessage.id);
                }
                // Add a system error message to the owning session (not the currently selected one)
                const errorMsg = this.#chatMessageService.createMessage({
                  senderId: ChatRole.System,
                  role: ChatRole.System,
                  content: GENERIC_ERROR_MESSAGE,
                  clipboardVersion: GENERIC_ERROR_MESSAGE,
                  system: true,
                  error: true,
                  currentUserId: this.#profileService.userProfile()?.profile.name!,
                  showExpansionPanel: true,
                  expansionPanelTitle: 'Error Processing Prompt',
                });
                if (targetSession) {
                  if (!targetSession.messages) targetSession.messages = [];
                  targetSession.messages.push(errorMsg);
                }
              }
            } else {
              // Completed without messages and not error — clean up the loading entry
              if (targetSession?.messages) {
                targetSession.messages = targetSession.messages.filter(o => o.useLoadingMessage !== true);
              }
            }

            // Persist owning session - wrap in try-catch to ensure cleanup happens even if persist fails
            if (targetSession) {
              try {
                await firstValueFrom(this.#apiService.persistSession(targetSession));
              } catch (persistError) {
                console.error('Error persisting session during polling cleanup:', persistError);
                // Continue with cleanup even if persist fails
              }
            }

            // Clear input data for owning session on success
            if (!isError && inflight.sessionId) {
              if (this.#chatSessionService.selectedSessionId() === inflight.sessionId) {
                this.#chatDocumentService.selectedDocuments.set([]);
              }
              this.#chatSessionService.setSessionInputData(inflight.sessionId, {
                text: '',
                documents: [],
                instruction: undefined,
              });
            }

            // Mark this message as finished - this MUST happen even if persist fails
            this.#inflightByMessageId.delete(response.messageId);

            // Update active submitting state for this session: if no more in-flight messages for it, clear its flag
            const stillActiveForSession = Array.from(this.#inflightByMessageId.values()).some(v => v.sessionId === inflight.sessionId);
            if (!stillActiveForSession) {
              this.#multiSessionService.completeSession(inflight.sessionId);
            }

            // If no more in-flight messages, stop polling and run cleanup
            if (this.#inflightByMessageId.size === 0) {
              this.#stopPollingEntries(isError);
            }
          } else {
            // In progress update
            locatedMessage.formatted = progress.answer ?? 'Processing...';
            locatedMessage.progressDetail = progress;
          }
        } else {
          // No progress for this messageId; optionally handle as idle
          const msg = targetSession?.messages?.find(p => p.id === response.messageId);
          if (msg) {
            msg.formatted = 'No response';
            msg.useLoadingMessage = false;
          }
          this.#inflightByMessageId.delete(response.messageId);
        }
      }
    } catch (err) {
      console.error('Error in polling (batched):', err);
      // Do not stop all polling on one error; a transient network hiccup should be retried next tick
    }
  }

  /**
 * Stops the polling for stream responses.
 * Clears the polling interval and sets it to null.
 * If there was an error, it restores the session configuration.
 */
  async #stopPollingEntries(isError: boolean = false) {
    if (this.pollEntriesInterval) {
      clearInterval(this.pollEntriesInterval);
      this.pollEntriesInterval = null;

      const hasInstruction = this.#automationStateService.instructionInProgress();
      const hasSnapshot = this.#chatSessionService.selectedSession()?.snapshotConfiguration;

      // If the prompt was from the Automation Library and we have a saved configuration,
      // we need to restore it to prevent unwanted changes from being persisted after errors
      if (hasInstruction && hasSnapshot) {
        await this.#chatUtilitiesService.restoreSessionConfig(isError);
        const selectedSession = this.#chatSessionService.selectedSession();
        await firstValueFrom(this.#apiService.persistSession(selectedSession!));
      }

      // Always clear the prompt from session input data if this was a successful completion and there was an instruction
      // This ensures that subsequent manual messages don't incorrectly trigger the instruction flow
      if (hasInstruction && !isError) {
        await this.#promptLibraryService.removeSelectedPromptForCurrentSession();
      }

      // Clear the instruction in progress
      this.#automationStateService.instructionInProgress.set(null);
    }
  }

  /**
 * Parses the choice message to extract citations and format the answer text.
 * Replaces citation links in the answer text with numbered references.
 */
  #parseChoiceMessage(answer: any): { citations: any[], markdownFormatText: any } {
    let answerText = answer.answer;
    const citationLinks = answerText.match(/\[(doc\d\d?\d?)]/g);

    const lengthDocN = "[doc".length;

    let filteredCitations: any[] = [];
    let linkUrls: string[] = [];

    citationLinks?.forEach((link: any) => {
      // Replacing the links/citations with number
      let citationIndex = link.slice(lengthDocN, link.length - 1);
      let citation = this.#cloneDeep(answer.citations[Number(citationIndex) - 1]);
      if (citation) {
        let linkUrlIdx = linkUrls.indexOf(citation.url);
        if (linkUrlIdx >= 0) {
          answerText = answerText.replaceAll(link, ` ^${linkUrlIdx + 1}^ `);
        } else if (!filteredCitations.find((c) => c.id === citationIndex)) {
          linkUrls.push(citation.url);
          answerText = answerText.replaceAll(link, ` ^${linkUrls.length}^ `);
          citation.id = citationIndex; // original doc index to de-dupe
          citation.reindex_id = linkUrls.length; // reindex from 1 for display
          filteredCitations.push(citation);
        }
      }
    });

    return {
      citations: filteredCitations,
      markdownFormatText: answerText
    };
  }

  /**
 * Creates a deep clone of the given object.
 * This method ensures that the original object is not mutated.
 */
  #cloneDeep(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * This checks for if any messages are currently hung up or stuck in progress in the UI.
   * if so, we set the message to a default error message and remove the loading message.
   * Error processing prompt.
   */
  checkForStuckInProgressMessages(): void {
    const selectedSessionMessages = this.#chatSessionService.selectedSession()?.messages
    if (selectedSessionMessages) {
      const assistantMessages = selectedSessionMessages.filter(msg => msg.role === ChatRole.Assistant);
      const mostRecentMessage = assistantMessages[assistantMessages.length - 1];

      selectedSessionMessages.forEach(msg => {
        if (msg.role === ChatRole.Assistant && (msg.clipboardVersion === '' && msg !== mostRecentMessage || msg.clipboardVersion === 'Processing...')) {
          msg.formatted = "<p>Error processing prompt.</p>";
          msg.clipboardVersion = "Error processing prompt.";
          msg.canSubmit = true;
          msg.useLoadingMessage = false;
          }
        }
      );

      const session = this.#chatSessionService.selectedSession();
      if (session) {
        session.messages = selectedSessionMessages;
      }
    }
  }

  /**
   * This gets the message needed when canceling a prompt submission.
   */
  getCanceledMessage(): IMessage | undefined {
    const messages = this.#chatMessageService.messages();

    if (messages && messages.length >= 2) {
      return messages[messages.length - 2];
    } else if (messages && messages.length === 1) {
      return messages[0];
    } else {
      return undefined;
    }
  }

  // TODO: Commenting out for now. it's not being used anywhere
  // this checks to see if we have a prompt currently in progress.
  // if so, we cancel the prompt and navigate to the selected nav item.
  // checkForPromptInProgress() {
  //   if (this.#chatStateService.isSubmitting()) {
  //     const messageToBeCancelled = this.getCanceledMessage();
  //     if (messageToBeCancelled) {
  //       this.cancelRetries(messageToBeCancelled);
  //     }
  //   }
  // }

  #resetValuesForSessionInputData() {
    // Clear all session input data including instruction after successful message completion
    this.#chatSessionService.setSessionInputData(this.#selectedSessionId, {
      text: '',
      documents: [],
      instruction: undefined // Clear the instruction context after completion
    });
  }

  /**
   * Creates a system message with session configuration changes.
   *
   * @param currentSession The current session before changes
   * @param updatedFields Array of formatted change descriptions
   * @param sessionId The ID of the session being updated
   * @returns boolean - Whether any changes were found and a message was added
   */
  async createConfigChangeSystemMessage(currentSession: IChatSession | null | undefined, updatedFields: string[], sessionId?: string | number): Promise<boolean> {
    if (updatedFields.length > 0) {
      const systemPrompt = `Configuration Updated by: User\n ${updatedFields.join("\n")}`;
      const currentUserId = this.#profileService.userProfile()?.profile.name!;

      this.#chatMessageService.addMessage({
        senderId: ChatRole.System,
        role: ChatRole.System,
        content: systemPrompt,
        canSubmit: true,
        canCopy: systemPrompt !== "Please continue",
        currentUserId,
        showExpansionPanel: true,
        expansionPanelTitle: "Session Configuration Updated",
        clipboardVersion: systemPrompt,
        system: true,
      });

      if (currentSession) {
        // Get the most up-to-date session from the service
        const latestSession = this.#chatSessionService.selectedSession();
        if (latestSession) {
          // Update the messages field of the latest session
          latestSession.messages = this.#chatMessageService.messages()!;

          // Persist the latest session
          await firstValueFrom(this.#apiService.persistSession(latestSession));

          // NOTE: In order for selectedSession() to be updated, we need to set the selectedSessionId along with the chatSessions.
          // This is because selectedSession() is a computed() that depends on both the chatSessions signal and the selectedSessionId signal.
          this.#chatSessionService.selectedSessionId.set(latestSession.id);
        } else {
          // Fallback to using the provided currentSession if latest is not available
          currentSession.messages = this.#chatMessageService.messages()!;
          await firstValueFrom(this.#apiService.persistSession(currentSession));
          this.#chatSessionService.selectedSessionId.set(currentSession.id);
        }
      }

      return true;
    }

    return false;
  }

  /**
   * Checks and handles sensitivity label mismatch between session and prompt.
   *
   * @param sessionId The session ID
   * @param newLabelId The new sensitivity label ID
   * @returns boolean - Whether a mismatch was found and handled
   */
  handleSensitivityLabelMismatch(sessionId: string | number, newLabelId: number): boolean {
    if (sessionId &&
        this.#promptLibraryService.hasSelectedPromptForSession(sessionId.toString())) {
      const sessionInputData = this.#chatSessionService.getSessionInputData(sessionId.toString());
      const promptInstruction = sessionInputData.instruction;

      // Check if the instruction has a different sensitivity label than the new session
      const isSensitivityLabelMismatch = promptInstruction?.details.sensitivityLabels?.find(
        (label: any) => label.id !== newLabelId
      );

      if (isSensitivityLabelMismatch) {
        const alertMessage = 'The Prompt you selected has been removed from your Session because its Sensitivity Label does not match the current Session\'s Sensitivity Label. If you change the Session\'s Sensitivity Label, Prompts with non-matching Sensitivity Labels will be disabled and removed from the Session.';
        this.#promptLibraryService.removeSelectedPromptForCurrentSession(true, alertMessage);
        return true;
      }
    }

    return false;
  }

  /**
   * Compares agent lists and formats changes for display.
   *
   * @param oldAgents Previous list of agent IDs
   * @param newAgents New list of agent IDs
   * @returns Promise<string | undefined> - Formatted change string or undefined if no changes
   */
  async formatAgentChanges(oldAgents: string[], newAgents: string[]): Promise<string | undefined> {
    // Check if the agent lists are different
    if (!oldAgents.every(agent => newAgents.includes(agent)) ||
        !newAgents.every(agent => oldAgents.includes(agent))) {

      try {
        // Get agent names for both current and updated agents
        const allAgentIds = [...new Set([...oldAgents, ...newAgents])];
        const agents = await firstValueFrom(this.#apiService.getAgentsByIds(allAgentIds));

        // Map agent IDs to their names
        const agentMap = new Map(
          agents.map((agent: any) => [agent.id, agent.name || "Unknown"])
        );

        // Get names for previous and new agents
        const oldAgentNames = oldAgents.map(id => agentMap.get(id) || "Unknown");
        const newAgentNames = newAgents.map(id => agentMap.get(id) || "Unknown");

        // Format agent names as comma-separated lists
        const oldAgentsFormatted = oldAgentNames.length > 0 ? oldAgentNames.join(", ") : "None";
        const newAgentsFormatted = newAgentNames.length > 0 ? newAgentNames.join(", ") : "None";

        return `- Agent(s): from '${oldAgentsFormatted}' to '${newAgentsFormatted}'`;
      } catch (err) {
        console.error("Error fetching agent names:", err);
        // Fallback to using agent IDs
        return `- Agent(s): from '${oldAgents.join(", ")}' to '${newAgents.join(", ")}'`;
      }
    }

    return undefined;
  }

  /**
   * Compares AI configuration (model, temperature, topP) and formats changes for display.
   *
   * @param currentSession The current session
   * @param newModel The new model configuration
   * @param newTemperature The new temperature value
   * @param newTopP The new topP value
   * @returns string[] - Array of formatted change descriptions
   */
  handleAiConfigMismatch(
    currentSession: IChatSession | null | undefined,
    newModel: { accountName: string; model: string } | undefined,
    newTemperature: number | undefined,
    newTopP: number | undefined
  ): string[] {
    const changes: string[] = [];

    // Check if model was changed
    if (
      currentSession?.model &&
      newModel &&
      (currentSession.model.accountName !== newModel.accountName ||
        currentSession.model.model !== newModel.model)
    ) {
      changes.push(
        `- Model: from ${currentSession.model.accountName} - ${currentSession.model.model} to ${newModel.accountName} - ${newModel.model}`
      );
    }

    // Check if temperature was changed
    if (
      currentSession &&
      newTemperature !== undefined &&
      currentSession.temperature !== newTemperature
    ) {
      changes.push(
        `- Temperature: from ${currentSession.temperature} to ${newTemperature}`
      );
    }

    // Check if topP was changed
    if (
      currentSession &&
      newTopP !== undefined &&
      currentSession.topP !== newTopP
    ) {
      changes.push(
        `- Top P: from ${currentSession.topP} to ${newTopP}`
      );
    }

    return changes;
  }

  /**
   * Shows a success alert with the given message.
   *
   * @param message The message to display
   */
  showSuccessAlert(message: string): void {
    this.#alertService.showAlert({
      message,
      type: AlertEnum.success,
    });
  }

  /**
   * Gets the string representation of a sensitivity label.
   *
   * @param label The sensitivity label
   * @returns The string representation
   */
  getSensitivityLabelString(label: ISensitivityLabel): string {
    return this.#agentService.getSensitivityLabelString(label);
  }


  /**
   * Opens the document manager dialog for managing chat attachments.
   *
   * This method configures and displays the `DocumentManagerComponent` dialog,
   * allowing users to add, remove, or view documents attached to the current chat session.
   * The dialog is opened in full-screen mode.
   */
  onOpenDocumentManager() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.width = "100vw";
    dialogConfig.height = "100vh";
    dialogConfig.maxWidth = "100vw";
    dialogConfig.maxHeight = "100vh";

    const dialogRef = this.dialog.open(DocumentManagerComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(() => {
      if (!this.#automationStateService.isWorkflowInProgress()) {
        const documents = this.#chatDocumentService.selectedDocuments();
        if (!this.#conversationStarterService.isStartConversation()) {
          this.#chatSessionService.updateSessionInputDataDocuments(documents);
        }
      }
    });
  }

    /**
   * Exports the current chat session messages as a DOCX file.
   *
   * This method combines all messages in the selected chat session into a single Markdown string,
   * prepending each message with its role for clarity. It then calls the ExportChatSessionService
   * to convert the Markdown to a DOCX file and triggers the download.
   *
   * The exported file will be named 'export-chat-session-md.docx'.
   */
  exportChat(): void {
    const messages = this.#chatSessionService.selectedSession()?.messages;
    // Combine all clipboardVersion values into a single Markdown string with role
    const combinedMarkdown = (messages ?? [])
      .filter(
        (msg) => msg.clipboardVersion && msg.clipboardVersion.trim() !== ""
      )
      // Add the role before the message.  This is to make it easier to read the messages in the exported document since
      // we can't have a background color for the text.
      .map((msg) => `**${msg.role}**\n${msg.clipboardVersion}`)
      .join("\n\n");

    // Call the exportChatSessionService with the combined markdown
    this.#exportChatSessionService.convertAndDownload(
      combinedMarkdown,
      "export-chat-session-md.docx"
    );
  }
}
